import { Rocket, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import type { AuditId } from "@/packages/types";
import { Button } from "@/components/ui";
import { WidgetCard, WidgetCardSkeleton } from "./WidgetCard";

interface GetStartedCardProps {
  checklistTotal: number;
  documentTotal: number;
  auditId: AuditId;
  onGoToChecklist: () => void;
  index?: number;
}

function getStorageKey(auditId: AuditId) {
  return `audit-get-started-dismissed-${auditId}`;
}

export function GetStartedCard({
  checklistTotal,
  documentTotal,
  auditId,
  onGoToChecklist,
  index,
}: GetStartedCardProps) {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(getStorageKey(auditId));
    if (stored === "true") {
      setDismissed(true);
    }
  }, [auditId]);

  // Hide when both have data
  if (checklistTotal > 0 && documentTotal > 0) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(getStorageKey(auditId), "true");
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      {!dismissed && (
        <motion.div
          key="get-started"
          exit={{ opacity: 0, scale: 0.95, height: 0 }}
          transition={{ duration: 0.2 }}
        >
          <WidgetCard
            size="S"
            title="Get Started"
            icon={<Rocket size={16} />}
            className="bg-gradient-to-r from-accent/[0.03] to-transparent border-accent/15 border-dashed"
            action={
              <button
                type="button"
                className="text-muted hover:text-secondary transition-colors"
                onClick={handleDismiss}
                aria-label="Dismiss get started card"
              >
                <X size={14} />
              </button>
            }
            index={index}
          >
            <div className="flex flex-col justify-between h-full">
              <p className="text-sm text-secondary">
                Start your audit by setting up your checklist
              </p>
              <Button variant="secondary" size="sm" onClick={onGoToChecklist}>
                Go to Checklist
              </Button>
            </div>
          </WidgetCard>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function GetStartedCardSkeleton({ index }: { index?: number }) {
  return <WidgetCardSkeleton size="S" index={index} />;
}
